/**
 * Date helpers for the rules. Dates are ISO strings (YYYY-MM-DD, YYYY-MM or YYYY) compared as strings, which is
 * exact for these formats and needs no library (the portals and the api must not diverge on a date-fns version).
 */

export type DateResolution = 'day' | 'month' | 'year'

const toResolution = (isoDate: string, resolution: DateResolution): string =>
  resolution === 'day' ? isoDate.substring(0, 10) : resolution === 'month' ? isoDate.substring(0, 7) : isoDate.substring(0, 4)

export type DateProblem = 'before1900' | 'future' | 'beforeBirth'

/**
 * Why a date the DBS receives is unacceptable, or null. Checked in the DBS's order: the year must start 19 or 20
 * (code 104), it must not be in the future (103), and it must not be before the applicant's date of birth (105).
 * `today` and `dateOfBirth` are cut to the date's own resolution before comparing, so a month in the birth month is
 * not "before born".
 */
export const dateProblem = (resolution: DateResolution, date: string | null | undefined, dateOfBirth: string | null | undefined, today: string): DateProblem | null => {
  if (date == null || date === '') return null
  const earliest = toResolution('1900-01-01', resolution)
  if (date < earliest) return 'before1900'
  if (date > toResolution(today, resolution)) return 'future'
  if (dateOfBirth != null && dateOfBirth !== '' && date < toResolution(dateOfBirth, resolution)) return 'beforeBirth'
  return null
}

/** `today` moved back `years` whole years; 29 February becomes 1 March, as the Date object rolls it */
export const yearsBefore = (today: string, years: number): string => {
  const d = new Date(`${today.substring(0, 10)}T00:00:00Z`)
  d.setUTCFullYear(d.getUTCFullYear() - years)
  return d.toISOString().substring(0, 10)
}

/** Has the applicant reached 16 by `today`? (DBS: 16 to 110 inclusive, code 108) */
export const isOver16 = (dateOfBirth: string, today: string): boolean => dateOfBirth <= yearsBefore(today, 16)

/** Is the applicant younger than 110 on `today`? */
export const isUnder110 = (dateOfBirth: string, today: string): boolean => dateOfBirth > yearsBefore(today, 110)

export type MonthRange = {
  /** YYYY-MM */
  from: string,
  /** YYYY-MM */
  to: string,
}

const monthIndex = (yearMonth: string): number => {
  const year = parseInt(yearMonth.substring(0, 4), 10)
  const month = parseInt(yearMonth.substring(5, 7), 10)
  return year * 12 + (month - 1)
}

/** True for a well-formed YYYY-MM with a real month */
export const isYearMonth = (value: string | null | undefined): value is string =>
  typeof value === 'string' && /^\d{4}-(0[1-9]|1[0-2])$/.test(value)

const mergeOverlapping = (ranges: MonthRange[]): MonthRange[] => {
  const sorted = ranges.slice().sort((a, b) => a.from < b.from ? -1 : a.from > b.from ? 1 : 0)
  const merged: MonthRange[] = []
  for (let i = 0; i < sorted.length; i++) {
    const current = sorted[i]
    const previous = merged.length > 0 ? merged[merged.length - 1] : undefined
    if (previous === undefined) { merged.push({ from: current.from, to: current.to }); continue }
    if (current.to <= previous.to) continue                       // inside the previous range
    if (current.from <= previous.to) { previous.to = current.to; continue } // overlaps or touches it
    merged.push({ from: current.from, to: current.to })
  }
  return merged
}

/**
 * The gaps between month ranges that are not continuous (a range ending the month another starts is continuous;
 * overlaps are allowed, as the DBS allows). Malformed ranges are ignored. Returns the gaps newest first.
 */
export const findGapsInMonthRanges = (ranges: MonthRange[]): MonthRange[] => {
  const valid = ranges.filter(r => isYearMonth(r.from) && isYearMonth(r.to) && r.from <= r.to)
  const merged = mergeOverlapping(valid)
  const gaps: MonthRange[] = []
  for (let i = merged.length - 1; i > 0; i--) {
    const later = merged[i]
    const earlier = merged[i - 1]
    if (earlier.to < later.from) gaps.push({ from: earlier.to, to: later.from })
  }
  return gaps
}

/**
 * Whole years from the earliest of the given months to `today` (the DBS wants five). Matches the portals' previous
 * date-fns computation: the difference in whole years between the first of the earliest month and today.
 */
export const yearsCoveredSince = (yearMonths: (string | null | undefined)[], today: string): number | undefined => {
  const valid = yearMonths.filter(isYearMonth).sort()
  if (valid.length === 0) return undefined
  const months = monthIndex(today.substring(0, 7)) - monthIndex(valid[0])
  return months < 0 ? 0 : Math.floor(months / 12)
}
